import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import { Badge } from '@flipster/react/design-system/badge';
import { Button } from '@flipster/react/design-system/button';
import Formatted from '@flipster/react/design-utils/formatted';
import { useBreakpoint } from '@flipster/react/misc/responsive/index';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';
import { UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';

import { useShowAppDownloadAlert } from '../../../shared/app-download-alert';
import { copyTradingBunja } from '../../../shared/copy-trading/state';
import { copyTradingHomeSegmentBunja } from '../../event';

export const MyCopyTradeBalance = () => {
  const { trackCopyTradingViewCopyActivityClick } = useBunja(copyTradingHomeSegmentBunja);
  const { myStatusAtom } = useBunja(copyTradingBunja);
  const myStatus = useAtomValue(myStatusAtom);
  const { isMobile } = useBreakpoint();
  const showAppDownloadAlert = useShowAppDownloadAlert();

  if (!myStatus || (myStatus.numOngoingCopies == 0 && myStatus.numStoppedCopies == 0)) return null;

  const handleViewActivityClick = () => {
    trackCopyTradingViewCopyActivityClick();
    if (isMobile) showAppDownloadAlert();
  };

  return (
    <div
      className={flex({
        p: 20,
        rounded: 8,
        bg: 'background.default.level-1',
        tabletDown: {
          gap: 10,
          flexDirection: 'column',
        },
        tablet: {
          alignItems: 'center',
          justifyContent: 'space-between',
        },
      })}
    >
      <div className={stack({ gap: 12 })}>
        <div className={hstack({ gap: 8 })}>
          <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-5' })}>
            <FormattedMessage id='Copy.r251107.Total_copying_balance_symbol' values={{ symbol: UsdtCurrency }} />
          </p>
          {myStatus.numOngoingCopies > 0 && (
            <Badge type='outline' color='accent'>
              <FormattedMessage id='Copy.r251107.Copying_number' values={{ number: myStatus.numOngoingCopies }} />
            </Badge>
          )}
        </div>
        <Formatted.Number
          value={myStatus.totalCopyingBalanceInUsdt}
          precision={UsdtPrecision}
          toFixed
          css={{ textStyle: 'Title/L/Monospace', color: 'content.default.level-5' }}
        />
        <div className={hstack({ gap: 4 })}>
          <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-5' })}>
            <FormattedMessage id='Copy.r251107.Total_unrealized_pnl_symbol' values={{ symbol: UsdtCurrency }} />
          </p>
          <Formatted.Number
            value={myStatus.totalOngoingCopiesUnrealizedPnlInUsdt}
            precision={UsdtPrecision}
            toFixed
            withColor
            withPlus
            css={{ textStyle: 'Text/L/Monospace/Regular' }}
          />
        </div>
      </div>
      <Button
        hierarchy='secondary'
        color='neutral'
        size='large'
        label={<FormattedMessage id='Copy.r251107.View_copy_activity' />}
        as={isMobile ? 'button' : 'a'}
        href={isMobile ? undefined : '/copy-trading/activity'}
        onClick={handleViewActivityClick}
      />
    </div>
  );
};
